/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  THE BREAKPOINT LADDER, AS ARITHMETIC. [P4-UX]
 *
 *  ⚠️ TAILWIND'S BREAKPOINTS MEASURE THE WINDOW. EVERY SPLIT IN THIS APP LIVES INSIDE
 *  `<main>`, WHICH IS 304px NARROWER THAN THAT.
 *
 *  That is the whole reason this file exists. `md:w-64` is 256px of sidebar and `sm:px-6`
 *  is 48px of gutter, so `lg:` (1024px) fired when `TwoColumnLayout` actually had 720px —
 *  and `detail`'s `400px + gap-6 + minmax(0,1fr)` then left the substance column 296px
 *  wide. The numbers in `lib/twoColumn.ts` were right about the CONTENT and had never
 *  subtracted the shell.
 *
 *  So content layout is stated in CONTENT pixels, with a CONTAINER query on `<main>`, and
 *  never with `sm:`/`md:`/`lg:`. Three consequences, all of them wanted:
 *
 *    • `@min-[880px]:` IS the statement. A reader does not have to subtract anything.
 *    • It is automatically right when the sidebar collapses to its 56px rail, which no
 *      media query can be.
 *    • It is CSS-only, so jsdom — which loads no stylesheet and stubs `matchMedia` to
 *      `matches: false` FOREVER — stays inert instead of silently taking the narrow branch
 *      in all 1420 unit tests. A width-branching *render* would do exactly that.
 *
 *  ⚠️ NOTHING HERE MAY BE READ AT RUNTIME TO BRANCH A RENDER. These constants exist so the
 *  arithmetic is testable and so the doc and the code cannot disagree. The moment a
 *  component reads a width to decide what to render, the rule above is broken.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/** `AppShell`'s expanded sidebar — `md:w-64`. */
export const SIDEBAR_EXPANDED_PX = 256;
/** `AppShell`'s collapsed rail — `md:w-14`. */
export const SIDEBAR_RAIL_PX = 56;
/** `<main>`'s own padding, both sides — `sm:px-6`. */
export const MAIN_GUTTER_PX = 48;

/**
 * ⚠️ AT 2560 THE ANSWER IS NOT STRETCHING. A form field 1400px wide is worse than one
 * 600px wide, not better; the extra width goes to gutters instead.
 *
 * 1920 rather than 1440 DELIBERATELY: it is a no-op at every rung below 2560, so five of
 * the six rungs see a zero-pixel change and the cap cannot regress the floor. The widest
 * table in the repository is about 1400px at `max-content`, so no list needs an exemption.
 */
export const CONTENT_MAX_PX = 1920;

export type ContentRung = {
  /** The browser window. */
  readonly window: number;
  /** What `<main>` actually gets with the sidebar expanded. */
  readonly content: number;
  /** …and with it collapsed to the rail. */
  readonly contentWithRail: number;
};

const rungFor = (window: number): ContentRung => ({
  window,
  content: Math.min(window - SIDEBAR_EXPANDED_PX - MAIN_GUTTER_PX, CONTENT_MAX_PX),
  contentWithRail: Math.min(window - SIDEBAR_RAIL_PX - MAIN_GUTTER_PX, CONTENT_MAX_PX),
});

/**
 * 1366×768 is THE FLOOR, not the smallest interesting size — the client demos on a laptop,
 * and the product was built and reviewed wide.
 *
 * ⚠️ [C1b §7] NAMED, so a threshold can be DERIVED from it rather than repeating the number.
 */
export const CONTENT_FLOOR_PX = 1366;

/** The ladder. */
export const CONTENT_RUNGS: readonly ContentRung[] = [CONTENT_FLOOR_PX, 1440, 1536, 1600, 1920, 2560].map(rungFor);

/**
 * Where `TwoColumnLayout`'s two variants start splitting, in content pixels.
 * Exported so `twoColumn.ts` and the ladder cannot drift apart.
 */
export const SPLIT_THRESHOLD_PX = {
  /** 400 identity column + 24 gap + 420 for a Name/Rank/Status table and its pagination. */
  detail: 880,
  /** 520 of field column whose labels do not wrap + 24 gap + 360 of summary. */
  form: 920,
  /** …and the same with the 440px summary a five-column requirement preview needs. */
  formWide: 1120,
  /**
   * [P7B.1 §2] The template library: a 320px tree beside its detail pane — the review site's own
   * `320px minmax(0,1fr)`. It reuses `detail`'s split rather than adding a rung: 320 + 24 leaves 536
   * for the pane, more than the 420 `detail`'s substance column is sized for.
   */
  tree: 880,
} as const;

/** `AppShell`'s opt-in right rail — the 320–360px column the brief asked the shell to hold. */
export const RAIL_WIDTH_PX = 336;

/**
 * ⚠️ [UX-FINAL] MEASURED ON THE SHELL ROW, NOT THE WINDOW — and that is the whole point.
 *
 * The rail used to be `xl:block`, a WINDOW breakpoint, in the one file whose entire subject
 * is that content layout is never stated in window pixels. It was blind to the sidebar and
 * it was the wrong number besides: at 1280 window with the sidebar out, `xl` handed the rail
 * its 336px and left `<main>` **640 content px** — below every split in the app.
 *
 * The row is `window − sidebar`, so a container query on it moves with the sidebar's
 * 256↔56 collapse, which no media query can. The rule the number states:
 *
 *   the rail may take its width only when what is LEFT still clears the widest content
 *   split, so a page may use the rail and `TwoColumnLayout` at the same time.
 *
 * It pays off exactly where it should. At the 1366 floor the row is 1110 → summary bar;
 * collapse the sidebar and the row is 1310 → the rail appears with 926 content px to spare.
 * Collapsing the sidebar BUYS you the rail.
 */
export const RAIL_THRESHOLD_PX = RAIL_WIDTH_PX + MAIN_GUTTER_PX + SPLIT_THRESHOLD_PX.form;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C1b §7] WHERE THE DASHBOARD WATERMARK APPEARS. "DESKTOP ONLY", AS A NUMBER.
 *
 *  Derived rather than chosen: the shell row is `window − sidebar`, so at the supported
 *  floor with the sidebar expanded it is 1110px. The rule the number states is therefore
 *
 *    the mark appears only when the content area is at least as wide as it is AT THE FLOOR,
 *
 *  which makes "desktop only" a property of how much room there is rather than of a device
 *  class nobody can measure. It falls away exactly where it should: at 1200 window with the
 *  sidebar out the row is 944 and the app is cramped, so the decoration goes; at 1366 with
 *  the sidebar COLLAPSED the row is 1310 and it stays. `RAIL_THRESHOLD_PX`'s argument, and
 *  the same container.
 *
 *  ⚠️ MEASURED ON `@container/shell`, NEVER ON THE WINDOW. §8 bans a width-branching RENDER
 *  outright — `vitest.setup.ts` stubs `matchMedia` to `matches: false` for ever, so one would
 *  silently take the narrow path in every unit test — and [UX-FINAL] specifically reversed an
 *  `xl:` in this very shell for being blind to the sidebar's 200px collapse.
 *
 *  ⚠️ ONE SIDE ONLY, SO [UX-FINAL]'s COMPLEMENTARITY GAP CANNOT OPEN. There is no `@max-`
 *  twin: the element is `hidden` by default and the `@min-` turns it on.
 *
 *  ⚠️ AND TAILWIND CANNOT READ A CONSTANT. The class carries the literal, so
 *  `layout.test.ts` pins the two against each other — `twoColumn.test.ts`'s idiom.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const WATERMARK_THRESHOLD_PX = CONTENT_FLOOR_PX - SIDEBAR_EXPANDED_PX;
